import { Client } from '@botpress/client'
import * as fs from 'fs/promises'
import * as path from 'path'
import * as os from 'os'
import { auth } from '../auth/index.js'

export interface HubCacheEntry {
  id: string
  name: string
  version: string
  updatedAt: string
  createdAt: string
  title?: string
  description?: string
  iconUrl?: string
  public: boolean
  visibility: 'public' | 'private' | 'unlisted'
  ownerWorkspace?: {
    id: string
    name: string
  }
  verificationStatus?: 'unapproved' | 'approved' | 'pending' | 'rejected'
}

export interface HubCacheData {
  lastUpdated: string
  integrations: HubCacheEntry[]
}

export class HubCache {
  private static readonly CACHE_FILE = 'botpress-hub-cache.json'
  private static readonly CACHE_DIR = path.join(os.homedir(), '.adk', 'cache')
  private static readonly CACHE_PATH = path.join(HubCache.CACHE_DIR, HubCache.CACHE_FILE)
  private static readonly CACHE_DURATION = 30 * 60 * 1000 // 30 minutes in milliseconds

  private client?: Client

  constructor() {}

  private async getClient(): Promise<Client> {
    if (!this.client) {
      const credentials = await auth.getActiveCredentials()

      if (!credentials.workspaceId) {
        throw new Error('No workspace ID found in current profile. Please login again with "adk login"')
      }

      this.client = new Client({
        token: credentials.token,
        apiUrl: credentials.apiUrl,
        workspaceId: credentials.workspaceId,
        headers: {
          'x-multiple-integrations': 'true',
        },
      })
    }
    return this.client
  }

  /**
   * Ensure cache directory exists
   */
  private async ensureCacheDir(): Promise<void> {
    try {
      await fs.mkdir(HubCache.CACHE_DIR, { recursive: true })
    } catch {
      // Directory might already exist, that's fine
    }
  }

  /**
   * Load cache from disk
   */
  private async loadCache(): Promise<HubCacheData | null> {
    try {
      const data = await fs.readFile(HubCache.CACHE_PATH, 'utf-8')
      return JSON.parse(data) as HubCacheData
    } catch {
      return null
    }
  }

  /**
   * Save cache to disk
   */
  private async saveCache(data: HubCacheData): Promise<void> {
    await this.ensureCacheDir()
    await fs.writeFile(HubCache.CACHE_PATH, JSON.stringify(data, null, 2))
  }

  /**
   * Check if cache needs refresh
   */
  private async needsRefresh(): Promise<boolean> {
    const cache = await this.loadCache()
    if (!cache) return true

    const lastUpdated = new Date(cache.lastUpdated)
    const now = new Date()
    const diff = now.getTime() - lastUpdated.getTime()

    return diff > HubCache.CACHE_DURATION
  }

  /**
   * Refresh the hub cache
   */
  async refresh(force: boolean = false): Promise<void> {
    // Check if refresh is needed
    if (!force && !(await this.needsRefresh())) {
      return
    }

    const client = await this.getClient()
    const cache = await this.loadCache()
    const latestByName = new Map<string, HubCacheEntry>()

    // Load existing cache into map (keyed by name for latest version tracking)
    if (cache) {
      for (const entry of cache.integrations) {
        latestByName.set(entry.name, entry)
      }
    }

    try {
      // Fetch integrations sorted by updatedAt descending
      for await (const integration of client.list.publicIntegrations({
        sortBy: 'updatedAt',
        direction: 'desc',
      })) {
        // Check if we have a newer version of this integration
        const existing = latestByName.get(integration.name)

        // If we've reached an integration with the same id and updatedAt we already have, stop
        if (existing && integration.id === existing.id && integration.updatedAt === existing.updatedAt) {
          break
        }

        if (
          !existing ||
          integration.version.localeCompare(existing.version, undefined, {
            numeric: true,
          }) > 0
        ) {
          // Add or update to latest version
          latestByName.set(integration.name, {
            id: integration.id,
            name: integration.name,
            version: integration.version,
            updatedAt: integration.updatedAt,
            createdAt: integration.createdAt,
            title: integration.title,
            description: integration.description,
            iconUrl: integration.iconUrl,
            public: integration.public,
            visibility: integration.visibility,
            ownerWorkspace: integration.ownerWorkspace
              ? {
                  id: integration.ownerWorkspace.id,
                  name: integration.ownerWorkspace.name,
                }
              : undefined,
            verificationStatus: integration.verificationStatus,
          })
        }
      }

      // Convert map back to array, sorted by updatedAt descending
      const integrations = Array.from(latestByName.values()).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))

      // If we fetched all integrations (didn't break early), we have a complete list
      // Otherwise, merge with existing cache as done above

      // Save the updated cache
      const newCache: HubCacheData = {
        lastUpdated: new Date().toISOString(),
        integrations,
      }

      await this.saveCache(newCache)
    } catch (error) {
      throw new Error(`Failed to refresh hub cache: ${error instanceof Error ? error.message : String(error)}`)
    }
  }

  /**
   * Get all integrations from cache
   */
  async getAllIntegrations(): Promise<HubCacheEntry[]> {
    // Ensure cache is fresh
    await this.refresh()

    const cache = await this.loadCache()
    return cache?.integrations || []
  }

  /**
   * Search integrations by name
   */
  async searchIntegrations(query: string): Promise<HubCacheEntry[]> {
    const integrations = await this.getAllIntegrations()
    const lowerQuery = query.toLowerCase()

    return integrations.filter(
      (integration) =>
        integration.name.toLowerCase().includes(lowerQuery) ||
        integration.title?.toLowerCase().includes(lowerQuery) ||
        integration.description?.toLowerCase().includes(lowerQuery)
    )
  }

  /**
   * Get integration by name (latest version)
   */
  async getIntegrationByName(name: string): Promise<HubCacheEntry | null> {
    const integrations = await this.getAllIntegrations()

    // Since we only keep latest versions, just find by name
    return integrations.find((i) => i.name === name) || null
  }

  /**
   * Get all versions of an integration
   * Note: Since we only cache latest versions, this returns only the latest
   */
  async getIntegrationVersions(name: string): Promise<string[]> {
    const integration = await this.getIntegrationByName(name)
    return integration ? [integration.version] : []
  }

  /**
   * Get cache statistics
   */
  async getStats(): Promise<{
    lastUpdated: Date | null
    integrationCount: number
    cacheSize: number
  }> {
    const cache = await this.loadCache()

    try {
      const stats = await fs.stat(HubCache.CACHE_PATH)
      return {
        lastUpdated: cache ? new Date(cache.lastUpdated) : null,
        integrationCount: cache?.integrations.length || 0,
        cacheSize: stats.size,
      }
    } catch {
      return {
        lastUpdated: null,
        integrationCount: 0,
        cacheSize: 0,
      }
    }
  }

  /**
   * Clear the cache
   */
  async clear(): Promise<void> {
    try {
      await fs.unlink(HubCache.CACHE_PATH)
    } catch {
      // File might not exist, that's fine
    }
  }
}
